import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, InputAdornment, MenuItem, TextField } from '@mui/material';
import { Button, LoadingButton } from '@apigrowfinance/pbolt-button';
import { PDialogAction, PDialogContent } from '@apigrowfinance/pbolt-dialog';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import * as yup from 'yup';

import { IPhoneDetailsFormInputs } from '@clients/accountsClient';
import phoneLocationTypes from '@messages/phoneLocationTypes';
import phoneValidations from '@messages/phoneValidations';
import getEnumTranslation from '@utils/getEnumTranslation';

interface IFormInputs {
    type: string;
    countryCode: string;
    areaCode: string;
    number: string;
    extension: string;
}

const schema = yup.object().shape({
    type: yup.string().required('PHONEFORM_TYPE'),
    countryCode: yup
        .number()
        .required('PHONEFORM_COUNTRYCODE')
        .typeError('PHONEFORM_COUNTRYCODE_NUMERIC'),
    areaCode: yup
        .string()
        .required('PHONEFORM_AREACODE')
        .max(5, 'PHONEFORM_AREACODE_LENGTH'),
    number: yup
        .string()
        .required('PHONEFORM_NUMBER')
        .min(4, 'PHONEFORM_NUMBER_MIN_LENGTH')
        .max(15, 'PHONEFORM_NUMBER_MAX_LENGTH'),
    extension: yup
        .string()
        .nullable()
        .max(10, 'PHONEFORM_EXTENSION_MAX_LENGTH'),
});

interface PhoneFormProps {
    isLoading: boolean;
    defaultValues: Record<string, unknown>;
    onClickClose: () => void;
    mutate?: ({}: IPhoneDetailsFormInputs) => void;
}

const PhoneForm = ({
    onClickClose,
    mutate,
    isLoading,
    defaultValues,
}: PhoneFormProps) => {
    const {
        control,
        handleSubmit,
        formState: { isDirty, errors },
    } = useForm<IFormInputs>({
        defaultValues: {
            ...defaultValues,
        },
        resolver: yupResolver(schema),
    });

    const typeOptions = [
        {
            label: 'Residential',
            value: 'RESIDENTIAL',
        },
        {
            label: 'Commercial',
            value: 'COMMERCIAL',
        },
        {
            label: 'Mobile',
            value: 'MOBILE',
        },
    ];

    const formSubmitHandler: SubmitHandler<IFormInputs> = (
        data: IFormInputs,
    ) => {
        const dataToSave: IPhoneDetailsFormInputs = {
            area_code: Number(data.areaCode),
            country_code: Number(data.countryCode),
            number: data.number,
            active: true,
            extension: data.extension,
            type: data.type,
        };
        mutate({ ...dataToSave });
    };

    const handleCloseForm = () => {
        onClickClose();
    };

    return (
        <form
            onSubmit={handleSubmit(formSubmitHandler)}
            style={{ width: '100%' }}>
            <PDialogContent>
                <Grid container>
                    <Grid
                        container
                        flexDirection="column"
                        justifyContent="space-evenly">
                        <Controller
                            name="type"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    error={!!errors.type}
                                    helperText={
                                        !!errors.type &&
                                        getEnumTranslation(
                                            errors.type?.message,
                                            {
                                                messages: phoneValidations,
                                            },
                                        )
                                    }
                                    id="phone-type"
                                    fullWidth
                                    select
                                    label={
                                        <FormattedMessage
                                            id="PhoneForm.type"
                                            defaultMessage="Type"
                                        />
                                    }
                                    onChange={onChange}
                                    value={value || ''}>
                                    {typeOptions?.map((option) => (
                                        <MenuItem
                                            key={option.label}
                                            value={option.value}>
                                            {getEnumTranslation(option.value, {
                                                messages: phoneLocationTypes,
                                            })}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                            }}>
                            <Box
                                sx={{
                                    justifyContent: 'space-between',
                                    flexDirection: 'column',
                                    width: '40%',
                                    mr: 2,
                                }}>
                                <Controller
                                    name="countryCode"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        +
                                                    </InputAdornment>
                                                ),
                                            }}
                                            label={
                                                <FormattedMessage
                                                    id="PhoneForm.countryCode"
                                                    defaultMessage="Country Code"
                                                />
                                            }
                                            error={!!errors.countryCode}
                                            helperText={
                                                errors.countryCode &&
                                                getEnumTranslation(
                                                    errors.countryCode?.message,
                                                    {
                                                        messages:
                                                            phoneValidations,
                                                    },
                                                )
                                            }
                                            variant="outlined"
                                        />
                                    )}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '60%',
                                }}>
                                <Controller
                                    name="areaCode"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={
                                                <FormattedMessage
                                                    id="PhoneForm.areaCode"
                                                    defaultMessage="Area Code"
                                                />
                                            }
                                            error={!!errors.areaCode}
                                            helperText={
                                                errors.areaCode &&
                                                getEnumTranslation(
                                                    errors.areaCode?.message,
                                                    {
                                                        messages:
                                                            phoneValidations,
                                                    },
                                                )
                                            }
                                            variant="outlined"
                                        />
                                    )}
                                />
                            </Box>
                        </Box>

                        <Controller
                            name="number"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={
                                        <FormattedMessage
                                            id="PhoneForm.number"
                                            defaultMessage="Number"
                                        />
                                    }
                                    error={!!errors.number}
                                    helperText={
                                        errors.number &&
                                        getEnumTranslation(
                                            errors.number?.message,
                                            {
                                                messages: phoneValidations,
                                            },
                                        )
                                    }
                                    variant="outlined"
                                />
                            )}
                        />
                        <Controller
                            name="extension"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={
                                        <FormattedMessage
                                            id="PhoneForm.extension"
                                            defaultMessage="Extension"
                                        />
                                    }
                                    error={!!errors.extension}
                                    helperText={
                                        errors.extension &&
                                        getEnumTranslation(
                                            errors.extension?.message,
                                            {
                                                messages: phoneValidations,
                                            },
                                        )
                                    }
                                    variant="outlined"
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </PDialogContent>

            <PDialogAction>
                <Button onClick={() => handleCloseForm()}>
                    {getEnumTranslation('cancel')}
                </Button>
                <LoadingButton
                    loading={isLoading}
                    disabled={!isDirty}
                    onClick={handleSubmit(formSubmitHandler)}
                    type="submit"
                    variant="contained">
                    {getEnumTranslation('save')}
                </LoadingButton>
            </PDialogAction>
        </form>
    );
};

export default PhoneForm;
